import deepmerge from "deepmerge";
import { cloneDeep, set } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { v4 } from "uuid";

import { getValue, setStyles } from "@intelligems/ig-plugin/core";
import { setDiscountQuantityVariables } from "@intelligems/ig-plugin/widgets";
import {
  DiscountQuantityWidget,
  DiscountQuantityWidgetDeviceConfig,
  DiscountQuantityWidgetLikeObject,
  DiscountQuantityWidgetVariables,
  DiscountQuantityWindowState,
  DottedJoin,
  ExperienceCurrencyDto,
  OfferDto,
  WidgetDto,
} from "@intelligems/ig-types";
import { DiscountQuantityWidgetBase } from "@intelligems/ig-widgets";
import { Box } from "@mui/material";

import { useConfiguration } from "../../../api/configurations/use-configuration";
import { ConfigType } from "../../../types/widgets";
import {
  DiscountQuantityWidgetButtonsStub,
  DiscountStateStub,
  DiscountStub,
} from "../stubs";

interface IgDiscountQuantityWidgetProps {
  configType: ConfigType;
  quantityWidget: DiscountQuantityWidget;
  discountState?: OfferDto;
  userInterfaceVariables?: DiscountQuantityWidgetVariables;
}

export const IgDiscountQuantityWidget = ({
  configType,
  quantityWidget,
  userInterfaceVariables,
  discountState,
}: IgDiscountQuantityWidgetProps) => {
  const isMobile = "mobile" === configType;
  const prefix = `ig-dq-${quantityWidget.id}`;

  const currencyFormat: ExperienceCurrencyDto =
    useConfiguration("currency_format")?.value;

  const DiscountState = discountState || DiscountStateStub;

  const formatter = new Intl.NumberFormat("en", {
    style: "currency",
    currency: currencyFormat?.code || "USD",
    maximumFractionDigits: 0,
  });

  const DiscountQuantityWidget = useMemo(() => {
    const resp = cloneDeep(quantityWidget);

    if (!resp.config.desktop?.variables?.textReplacements) {
      set(resp, "config.desktop.variables.textReplacements", {});
    }

    if (
      !resp.config.desktop?.variables?.buttons &&
      !userInterfaceVariables?.buttons &&
      !isMobile
    ) {
      set(
        resp,
        "config.desktop.variables.buttons",
        DiscountQuantityWidgetButtonsStub
      );
    }

    if (userInterfaceVariables) {
      resp.config.desktop!.variables! = deepmerge(
        resp.config.desktop?.variables || {},
        userInterfaceVariables || {}
      );
    }

    for (const button of resp.config.desktop?.variables?.buttons || []) {
      if (!button.extraMessage) {
        button.extraMessage = "";
      }
    }

    resp.config.desktop!.variables = setDiscountQuantityVariables(
      resp.config.desktop!.variables!
    );

    return resp;
  }, [
    quantityWidget,
    quantityWidget?.config?.desktop?.variables,
    userInterfaceVariables,
    isMobile,
  ]);

  const DiscountQuantityWidgetStub =
    useCallback((): DiscountQuantityWidgetLikeObject => {
      const state: WidgetDto = {
        id: v4(),
        config: {},
        name: "",
        enabledSitewide: false,
        widgetType: "quantityButtons",
      };
      return {
        ...state,
        config: state.config || {},
        setStyles(): void {},
        toModel(): WidgetDto {
          return state;
        },
      };
    }, [DiscountQuantityWidget]);

  const [windowState, setWindowState] = useState<DiscountQuantityWindowState>(
    () => {
      const widget = DiscountQuantityWidgetStub();
      return {
        discount: DiscountStub(DiscountState),
        widget,
        selectedButtonIndex: 0,
        buttonState: (widget.config.desktop?.variables?.buttons || []).map(
          () => ({
            unitDiscount: 0,
            discountedUnitPrice: "",
          })
        ),
        selectedProduct: {
          productId: "",
          variantId: "",
          basePrice: 0,
        },
      };
    }
  );

  const getWidgetConfig = (
    path: DottedJoin<DiscountQuantityWidgetDeviceConfig>
  ) => {
    let value;
    if (isMobile) {
      value = getValue(DiscountQuantityWidget.config.mobile, path as string);
    }

    if (!value) {
      value = getValue(DiscountQuantityWidget.config.desktop, path as string);
    }
    return value;
  };

  const handleButtonClick = useCallback((index: number, quantity: number) => {
    setWindowState({ ...windowState, selectedButtonIndex: index });
  }, []);

  useEffect(() => {
    setStyles(
      prefix,
      DiscountQuantityWidget?.config?.desktop?.styles,
      DiscountQuantityWidget?.config?.mobile?.styles
    );
  }, [
    DiscountQuantityWidget?.config?.desktop?.styles,
    DiscountQuantityWidget?.config?.mobile?.styles,
    prefix,
  ]);

  useEffect(() => {
    let buttonIndex = 0;
    const newWindowState = cloneDeep(windowState);
    for (const button of DiscountQuantityWidget.config.desktop?.variables
      ?.buttons || []) {
      for (const tier of DiscountState.tiers) {
        if (
          button.quantity >= tier.minimumUnits &&
          button.quantity <= tier.maximumUnits
        ) {
          newWindowState.buttonState[buttonIndex] = {
            unitDiscount: tier.unitDiscount,
            discountedUnitPrice: formatter.format(100 - tier.unitDiscount),
          };
          break;
        }
      }

      if (!newWindowState.buttonState[buttonIndex]) {
        newWindowState.buttonState[buttonIndex] = {
          unitDiscount: 0,
          discountedUnitPrice: formatter.format(100),
        };
      }

      buttonIndex++;
    }
    setWindowState(newWindowState);
  }, [DiscountState]);

  const handleMessage = () => {};
  return (
    <Box sx={{ marginLeft: "-5px", marginRight: "-5px" }}>
      <DiscountQuantityWidgetBase
        config={DiscountQuantityWidget.config}
        isMobile={isMobile}
        prefix={prefix}
        windowState={windowState}
        handleMessage={handleMessage}
        getWidgetConfig={getWidgetConfig}
        currencyFormatter={formatter.format}
        handleButtonClick={handleButtonClick}
      />
    </Box>
  );
};
